import type { ReactElement } from 'react'
import { useEffect, useState } from 'react'

import { useApi } from '../ApiProvider'
import { useVideoTutorialSetter, useVideoTutorialState } from './VideoTutorialContext'
import ModalDialog from '../ModalDialog'

const TUTORIALS = {
  de: {
    themes: 'sv-epnow-design-vorlage-de',
    pages: 'sv-epnow-seiten-de',
    content: 'sv-epnow-inhaltselemente-de',
    legalpages: 'sv-epnow-rechtliche-seiten-de',
  },
  en: {},
} as const

export type Tutorial = keyof (typeof TUTORIALS)['de']

export default function VideoTutorialModal({ t }: Readonly<TranslateProps>): ReactElement | null {
  const tutorial = useVideoTutorialState()
  const setter = useVideoTutorialSetter()

  const handleClose = () => {
    setter(undefined)
  }

  if (!tutorial) return null

  return (
    <ModalDialog isOpen onRequestClose={handleClose} className="video-tutorial-modal">
      <div className="ep-modal-header">
        <span className="ep-modal-close" onClick={handleClose} />
      </div>
      <ModalDialog.Body>
        <VideoTutorialIframe tutorial={tutorial} t={t} />
      </ModalDialog.Body>
    </ModalDialog>
  )
}

type VideoTutorialIframeProps = {
  tutorial: Tutorial
} & TranslateProps

function VideoTutorialIframe({ tutorial, t }: Readonly<VideoTutorialIframeProps>): ReactElement | null {
  const [src, setSrc] = useState<string | null>(null)

  const api = useApi()

  useEffect(() => {
    async function resolveVideoSrcUrl() {
      try {
        const { data } = await api.get<{
          resolvedUrl: string
        }>(`/api/v2/resolve-epages-short-url`, {
          params: {
            id: TUTORIALS.de[tutorial],
          },
        })

        setSrc(data.resolvedUrl)
      } catch {
        // fail silently
      }
    }

    resolveVideoSrcUrl()
  }, [tutorial, api])

  if (!src) return null

  return (
    <iframe
      src={src}
      width="640"
      height="360"
      title={t('themeManagerSection.videoTutorial.videoTutorialModal.accessibilityLabel')}
      style={{ border: 'none' }}
      allow="autoplay; fullscreen; picture-in-picture"
    />
  )
}
